import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import api from '../../services/api';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Title = styled.h2`
  margin: 0 0 8px;
`;

const EmptyState = styled.div`
  background: white;
  border-radius: 8px;
  padding: 48px 24px;
  text-align: center;
  color: #6b7280;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
`;

const Card = styled.div`
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border-left: 4px solid ${({ $status }) =>
    $status === 'active' ? '#2e7d32' :
    $status === 'pending' ? '#ed6c02' :
    $status === 'suspended' ? '#9e9e9e' :
    '#d32f2f'};
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 12px;
`;

const BenefitName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
`;

const CategoryName = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
`;

const StatusBadge = styled.span`
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ $status }) =>
    $status === 'active' ? '#2e7d3215' :
    $status === 'pending' ? '#ed6c0215' :
    $status === 'suspended' ? '#9e9e9e15' :
    '#d32f2f15'};
  color: ${({ $status }) =>
    $status === 'active' ? '#2e7d32' :
    $status === 'pending' ? '#ed6c02' :
    $status === 'suspended' ? '#9e9e9e' :
    '#d32f2f'};
`;

const Description = styled.div`
  font-size: 13px;
  color: #4b5563;
  margin: 8px 0;
`;

const Meta = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f3f4f6;
  font-size: 12px;
`;

const MetaLabel = styled.div`
  color: #9ca3af;
`;

const MetaValue = styled.div`
  color: #1f2937;
  font-weight: 500;
`;

export default function MyBenefitsPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/v1/my-benefits')
      .then((res) => setItems(res.data?.data?.data || []))
      .finally(() => setLoading(false));
  }, []);

  const statusLabels = useMemo(() => ({
    active: t('myBenefit.statusActive'),
    pending: t('myBenefit.statusPending'),
    suspended: t('myBenefit.statusSuspended'),
    terminated: t('myBenefit.statusTerminated'),
  }), [t]);

  const periodicityLabels = useMemo(() => ({
    monthly: t('benefit.periodicityMonthly'),
    yearly: t('benefit.periodicityYearly'),
    'one-time': t('benefit.periodicityOneTime'),
    'on-demand': t('benefit.periodicityOnDemand'),
    continuous: t('benefit.periodicityContinuous'),
  }), [t]);

  if (loading) return null;

  return (
    <Container>
      <Title>{t('myBenefit.title')}</Title>

      {items.length === 0 ? (
        <EmptyState>{t('myBenefit.empty')}</EmptyState>
      ) : (
        <CardGrid>
          {items.map((b) => (
            <Card key={b.id} $status={b.status}>
              <CardHeader>
                <div>
                  <BenefitName>{b.benefitName}</BenefitName>
                  <CategoryName>{b.categoryName}</CategoryName>
                </div>
                <StatusBadge $status={b.status}>{statusLabels[b.status] || b.status}</StatusBadge>
              </CardHeader>

              {b.description && <Description>{b.description}</Description>}

              <Meta>
                <MetaLabel>{t('myBenefit.activationDate')}</MetaLabel>
                <MetaValue>{b.activationDate ? new Date(b.activationDate).toLocaleDateString('tr-TR') : '-'}</MetaValue>

                <MetaLabel>{t('myBenefit.periodicity')}</MetaLabel>
                <MetaValue>{periodicityLabels[b.periodicity] || b.periodicity}</MetaValue>

                {b.monthlyCostEmployee != null && (
                  <>
                    <MetaLabel>{t('myBenefit.contribution')}</MetaLabel>
                    <MetaValue>{Number(b.monthlyCostEmployee).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</MetaValue>
                  </>
                )}

                {b.provider && (
                  <>
                    <MetaLabel>{t('myBenefit.provider')}</MetaLabel>
                    <MetaValue>{b.provider}</MetaValue>
                  </>
                )}

                {b.supportsDependents && (
                  <>
                    <MetaLabel>{t('myBenefit.dependentCount')}</MetaLabel>
                    <MetaValue>{b.dependentCount}</MetaValue>
                  </>
                )}
              </Meta>
            </Card>
          ))}
        </CardGrid>
      )}
    </Container>
  );
}
